// src/pages/returnNote/CreateReturnNotePage.jsx
// Replicates the original PosReturnModal flow (src/pages/pos/components/PosReturnModal.jsx) as a
// full page: look up the original order, select items/quantities to return, choose a reason and
// refund method, then submit via createReturnNote.
import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Save, RotateCcw, X, Search, AlertCircle, Package, ShoppingBag, User, Receipt } from 'lucide-react';
import { createReturnNote } from '../../services/api/offlineSales/returnNotes';
import { getOrder, getOrders } from '../../services/api/offlineSales/orders';
import { useLocalization } from '../../contexts/LocalizationContext';
import { useAlert } from '../../contexts/AlertContext';
import { formatCurrency } from '../../utils/orderCalculations';
import { getImageUrl } from '../../utils/imageUtils';
import Dropdown from '../../components/ui/Dropdown/Dropdown';
import { InfoCard } from '../../components/common';
import ProductThumb from '../procurement/components/ProductThumb';
import ps from '../procurement/components/ProcurementDetails.module.css';
import styles from './CreateReturnNotePage.module.css';

const CreateReturnNotePage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t } = useLocalization();
  const { success, error } = useAlert();

  const [orderIdInput, setOrderIdInput] = useState(searchParams.get('orderNumber') || '');
  const [order, setOrder] = useState(null);
  const backTarget = order
    ? `/offline-sales/orders/${order.id}`
    : '/offline-sales/return-notes';
  const [isLookingUp, setIsLookingUp] = useState(false);
  const [lookupError, setLookupError] = useState('');
  const [orderOptions, setOrderOptions] = useState([]);
  const [isSearchingOrder, setIsSearchingOrder] = useState(false);

  const [returnItems, setReturnItems] = useState([]);
  const [refundMethod, setRefundMethod] = useState('cash');
  const [reason, setReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (searchParams.get('orderNumber')) {
      handleLookupOrder(searchParams.get('orderNumber'));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const buildReturnItems = (loadedOrder) => (loadedOrder.orderItems || []).map(item => ({
    orderItemId: item.id,
    productId: item.productId,
    variationId: item.variationId,
    name: item.Product?.name + (item.ProductVariation?.sku ? ` — ${item.ProductVariation.sku}` : ''),
    image: item.Product?.image || null,
    // Delivery-aware, backend-computed (offlineOrder.service.js getOrderById) — matches exactly
    // what returnNote.service.js validates against, so this never disagrees with the server.
    maxQty: parseFloat(item.availableToReturn ?? 0),
    deliveredQty: parseFloat(item.deliveredQty ?? 0),
    returnQty: parseFloat(item.availableToReturn ?? 0),
    unitPrice: parseFloat(item.unitPrice),
    lineDiscount: parseFloat(item.lineDiscount || 0),
  }));

  const toOrderOption = (o) => ({
    value: o.id,
    label: `${o.orderNumber}${o.customer?.name ? ` — ${o.customer.name}` : ''}`,
  });

  // Searchable lookup, same as GRN/RTV create pages: orders matching the typed order number; an
  // all-digit term is also resolved as an order id. Selecting an option runs handleLookupOrder.
  const searchOrders = async (term = '') => {
    const value = term.trim();
    try {
      setIsSearchingOrder(true);
      const { orders } = await getOrders({ search: value || undefined, limit: 20 });
      const results = (orders || []).filter(o => o.status !== 'cancelled');
      if (/^d+$/.test(value) && !results.some(o => String(o.id) === value)) {
        try {
          const byId = await getOrder(value);
          if (byId && byId.status !== 'cancelled') results.unshift(byId);
        } catch (err) {
          // not a valid id — number search results only
        }
      }
      setOrderOptions(results.map(toOrderOption));
    } catch (err) {
      console.error('Failed to search orders:', err);
      setOrderOptions([]);
    } finally {
      setIsSearchingOrder(false);
    }
  };

  useEffect(() => {
    searchOrders('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLookupOrder = async (idOverride) => {
    const orderIdValue = (idOverride ?? orderIdInput).trim();
    if (!orderIdValue) return;
    try {
      setIsLookingUp(true);
      setLookupError('');
      // A numeric value is the real numeric primary key — look it up directly. Anything else
      // (a user-typed order number, or the ?orderNumber= pre-fill from the order list/detail
      // pages, e.g. "VSS2609110002") is not a valid id, so search by order number instead of
      // feeding a non-numeric value into the numeric-id endpoint.
      let result;
      if (/^\d+$/.test(orderIdValue)) {
        result = await getOrder(orderIdValue);
      } else {
        const { orders } = await getOrders({ search: orderIdValue });
        const match = (orders || []).find(o => o.orderNumber?.toLowerCase() === orderIdValue.toLowerCase());
        if (!match) throw new Error(t('returnNote.orderNotFound'));
        // The search/list endpoint returns a lightweight summary (no nested Product/
        // ProductVariation per item) — fetch the full order by id, same as the numeric path,
        // so buildReturnItems gets complete item data instead of "undefined" names.
        result = await getOrder(match.id);
      }
      setOrder(result);
      setOrderOptions(prev => (prev.some(option => option.value === result.id) ? prev : [toOrderOption(result), ...prev]));
      setOrderIdInput(result.orderNumber || String(result.id));
      setReturnItems(buildReturnItems(result));
    } catch (err) {
      setOrder(null);
      setReturnItems([]);
      setLookupError(err.response?.data?.message || err.message || t('returnNote.orderNotFound'));
    } finally {
      setIsLookingUp(false);
    }
  };

  const updateQty = (idx, qty) => {
    setReturnItems(prev => prev.map((item, i) => {
      if (i !== idx) return item;
      const clamped = Math.min(Math.max(0, parseFloat(qty) || 0), item.maxQty);
      return { ...item, returnQty: clamped };
    }));
  };

  const totalRefund = returnItems.reduce((sum, item) => {
    if (item.returnQty <= 0) return sum;
    const unitNet = item.unitPrice - (item.lineDiscount / (item.maxQty || 1));
    return sum + Math.max(0, unitNet * item.returnQty);
  }, 0);

  const hasItems = returnItems.some(i => i.returnQty > 0);

  const validateForm = () => {
    const newErrors = {};
    if (!order) newErrors.order = t('returnNote.orderRequired');
    if (!hasItems) newErrors.items = t('returnNote.atLeastOneItemRequired');
    if (!reason.trim()) newErrors.reason = t('returnNote.reasonRequired');
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    if (!validateForm()) {
      error(t('returnNote.someFieldsRequired'), { title: t('common.validationError') });
      return;
    }

    setIsSubmitting(true);
    try {
      const items = returnItems
        .filter(i => i.returnQty > 0)
        .map(i => ({
          orderItemId: i.orderItemId,
          quantityReturned: i.returnQty,
        }));

      const payload = {
        originalOrderId: order.id,
        shopId: order.shopId,
        reason: reason.trim(),
        refundMethod,
        returnItems: items,
      };

      const response = await createReturnNote(payload);
      success(t('returnNote.returnCreatedSuccess', { number: response.return?.returnNumber }), {
        title: t('common.success'),
      });
      navigate(`/offline-sales/return-notes/${response.return.id}`);
    } catch (err) {
      console.error('Return note creation error:', err);
      error(err.response?.data?.message || t('returnNote.failedToCreateReturnNote'), {
        title: t('common.error'),
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={styles.createReturnNotePage}>
      <div className={styles.pageHeader}>
        <div className={styles.headerContent}>
          <div className={styles.headerTitle}>
            <RotateCcw className={styles.headerIcon} size={22} />
            <div className={styles.titleContent}>
              <h1>{t('returnNote.processReturn')}</h1>
              <p>{t('returnNote.processReturnDescription')}</p>
            </div>
          </div>
          <button onClick={() => navigate(backTarget)} className={styles.backButton}>
            <ArrowLeft size={16} />
            {t('returnNote.backToReturnNotes')}
          </button>
        </div>
      </div>

      <form onSubmit={handleSubmit} className={styles.returnNoteForm}>
        <InfoCard icon={Search} title={t('returnNote.lookupOrder')}>
            <div className={styles.lookupRowWrapper}>
              <div className={styles.lookupRow}>
                <Dropdown
                  className={styles.lookupDropdown}
                  value={order?.id || ''}
                  onChange={value => { if (value) handleLookupOrder(String(value)); }}
                  options={orderOptions}
                  placeholder={isLookingUp ? t('common.loading') : t('returnNote.orderIdPlaceholder')}
                  searchPlaceholder={t('returnNote.searchOrder')}
                  onSearch={searchOrders}
                  loading={isSearchingOrder}
                  loadingText={t('common.loading')}
                  noOptionsText={t('returnNote.noOrdersFound')}
                  allowClear={false}
                  disabled={isLookingUp}
                />
              </div>
              {order && (
                <div className={styles.orderSummary}>
                  <span className={styles.summaryItem}><ShoppingBag size={14} /><strong>{order.orderNumber}</strong></span>
                  <span className={styles.summaryItem}><User size={14} />{order.customer?.name || t('common.na')}</span>
                  <span className={styles.summaryItem}><Receipt size={14} />{formatCurrency(order.grandTotal)}</span>
                </div>
              )}
            </div>
            {lookupError && <div className={styles.sectionError}><AlertCircle size={16} />{lookupError}</div>}
            {errors.order && <div className={styles.sectionError}><AlertCircle size={16} />{errors.order}</div>}
        </InfoCard>

        {order && (
          <div className={styles.topColumns}>
            <div className={styles.leftColumn}>
              <InfoCard icon={RotateCcw} title={t('returnNote.details')}>
                  <div className={styles.fieldGroup}>
                    <label>{t('returnNote.refundMethod')}</label>
                    <Dropdown
                      value={refundMethod}
                      onChange={value => setRefundMethod(value || 'cash')}
                      options={[
                        { value: 'cash', label: t('returnNote.refundMethod_cash') },
                        { value: 'credit_note', label: t('returnNote.refundMethod_credit_note') },
                        { value: 'loyalty_points', label: t('returnNote.refundMethod_loyalty_points') },
                      ]}
                      allowClear={false}
                    />
                  </div>
                  <div className={styles.fieldGroup}>
                    <label>{t('returnNote.reason')} *</label>
                    <textarea
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                      rows={3}
                      className={`${styles.textarea} ${errors.reason ? styles.error : ''}`}
                      placeholder={t('returnNote.reasonPlaceholder')}
                    />
                    {errors.reason && <span className={styles.errorText}>{errors.reason}</span>}
                  </div>
                  {hasItems && (
                    <div className={styles.refundPreview}>
                      <span>{t('returnNote.totalRefund')}</span>
                      <strong>{formatCurrency(totalRefund)}</strong>
                    </div>
                  )}
              </InfoCard>
            </div>

            <div className={styles.rightColumn}>
              <InfoCard icon={Package} title={t('returnNote.itemsToReturn')}>
                  <div className={ps.tableWrapper}>
                  <table className={ps.itemsTable}>
                    <thead>
                      <tr>
                        <th>{t('returnNote.item')}</th>
                        <th className={ps.alignRight}>{t('returnNote.available')}</th>
                        <th className={ps.alignRight}>{t('returnNote.returnQty')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {returnItems.map((item, idx) => (
                        <tr key={item.orderItemId} className={item.maxQty <= 0 ? styles.disabledRow : ''}>
                          <td>
                            <div className={ps.productCellInner}>
                              <ProductThumb src={getImageUrl(item.image)} alt={item.name || ''} />
                              <div className={ps.productCell}>
                                <span className={ps.productName}>{item.name}</span>
                              </div>
                            </div>
                          </td>
                          <td className={`${ps.alignRight} ${ps.amountCell} ${ps.amountStrong}`}>
                            {item.maxQty}
                            {item.maxQty <= 0 && item.deliveredQty <= 0 && (
                              <div className={ps.productSku}>{t('returnNote.notYetDelivered') || 'Not yet delivered'}</div>
                            )}
                          </td>
                          <td className={ps.alignRight}>
                            <input
                              type="number"
                              min="0"
                              max={item.maxQty}
                              step="1"
                              value={item.returnQty}
                              onChange={(e) => updateQty(idx, e.target.value)}
                              disabled={item.maxQty <= 0}
                              className={styles.qtyInput}
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  </div>
                  {errors.items && <div className={styles.sectionError}><AlertCircle size={16} />{errors.items}</div>}
              </InfoCard>
            </div>
          </div>
        )}

        <div className={styles.actionBarSpacer} />
      </form>

      <div className={styles.fixedActionBar}>
        <div className={styles.actionBarInner}>
          <div className={styles.actionBarMeta}>
            {hasItems && (
              <span className={styles.actionBarTotal}>
                {t('returnNote.totalRefund')}: <strong>{formatCurrency(totalRefund)}</strong>
              </span>
            )}
          </div>
          <div className={styles.actionBarButtons}>
            <button type="button" onClick={() => navigate(backTarget)} className={styles.cancelButton} disabled={isSubmitting}>
              <X size={15} />
              {t('common.cancel')}
            </button>
            <button type="button" onClick={handleSubmit} className={styles.submitButton} disabled={isSubmitting || !order}>
              {isSubmitting ? <div className={styles.loadingSpinner} /> : (
                <>
                  <Save size={15} />
                  {t('returnNote.confirmReturn')}
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateReturnNotePage;
